import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { authFetch } from 'api/auth/authFetch'

/**
 * Formats date string to YYYY-MM-DD format
 */
const formatDate = (dateString) => {
    if (!dateString) return null
    const date = new Date(dateString)
    return date.toISOString().split('T')[0]
}

/**
 * Maps API response to frontend contact model
 */
const mapContactFromApi = (apiContact) => {
return {
    id: apiContact.contactId,
    contactId: apiContact.contactId,  // needed for lazy loading API
    code: apiContact.code,
    authorKey: apiContact.authorKey,
    correspondenceName: apiContact.correspondenceName,
    contactType: apiContact.contactType,
    contactTypeId: apiContact.contactTypeId,
    companyType: apiContact.companyType,
    names: (apiContact.contactNames || []).map((name) => ({
            lineNum: name.lineNum,
            name: name.fullName,
            type: name.nameType,
            typeSapCode: name.nameTypeSapCode,
            typeId: name.nameTypeId,
            ipiNumber: name.ipiNumber || null
        })),
        memberships: (apiContact.memberships || []).map((membership) => ({
            lineNum: membership.lineNum,
            membershipType: membership.membershipType,
            membershipTypeId: membership.membershipTypeId,
            startDate: formatDate(membership.startDate)
        })),
        works: (apiContact.works || []).map((work, idx) => ({
            id: `w${idx + 1}`,
            type: work.domain,
            title: work.workTitle,
            episodeTitle: work.episodeTitle || null,
            contribution: work.contributionType,
            isbn: null,
            issn: null
        })),
        lazyLoadWorks: apiContact.lazyLoadWorks || false,
        worksCount: apiContact.worksCount || 0
    }
}

const fetchData = async (searchTerm, count, page, filter, sort, endpoint, controller) => {
    if (searchTerm == null || count === 0 || page === 0) return null

    const queryParams = new URLSearchParams({
        term: searchTerm,
        pageNumber: page.toString(),
        count: count.toString(),
        ...(filter && { filter: filter.toString() }),
        ...(sort && { sort: sort.toString() })
    }).toString()

    const url = `${endpoint}/search?${queryParams}`
    let responseCode = -1

    try {
        const response = await authFetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal
        })

        if (controller.signal?.aborted) return null

        responseCode = response.status
        if (responseCode === 200) return await response.json()

        throw {
            Message: 'The Contact Search fetch request was not successful',
            StatusCode: responseCode,
            Details: await response.text()
        }
    } catch (error) {
        if (error.name === 'AbortError') return null
        throw {
            Message: 'The Contact Search fetch request was not successful',
            StatusCode: responseCode,
            Details: error
        }
    }
}

const setUpFetch = (searchTerm, count, page, filter, sort, endpoint) => {
    const controller = new AbortController()
    return {
        controller,
        fetch: () => fetchData(searchTerm, count, page, filter, sort, endpoint, controller)
    }
}

const ContactSearchApi = (props) => {
    const [abortController, setAbortController] = useState(null)
    const [prevParams, setPrevParams] = useState({ page: -1, count: 50, sort: null, filter: null, searchTerm: null })
    const { setStatusAction, setResultsAction, requestSelector } = props

    const dispatch = useDispatch()
    const setStatus = (newStatus) => dispatch(setStatusAction(newStatus))
    const setResults = (results, totalCount) => dispatch(setResultsAction(results, totalCount))

    const apiRequest = useSelector(requestSelector)
    const { count, page, filter, sort, url, status, searchTerm } = apiRequest

    // Skip if no search initiated or params haven't changed
    if (searchTerm == null || (prevParams.page === page && prevParams.count === count && prevParams.filter === filter && prevParams.sort === sort && prevParams.searchTerm === searchTerm)) {
        return undefined
    }

    setPrevParams({ page, count, filter, sort, searchTerm })

    if (status === 'pending' && abortController) abortController.abort()
    if (status.status === 'pending') return

    setStatus('pending')
    const { controller, fetch } = setUpFetch(searchTerm, count, page, filter, sort, url)
    setAbortController(controller)

    fetch()
        .then(data => {
            if (data) {
                const mappedResults = (data.results || []).map(mapContactFromApi)
                setResults(mappedResults, data.totalCount || 0)
                setStatus('complete')
            }
        })
        .catch(error => {
            setResults([], 0)
            setStatus('error')
            console.error('Contact search error:', error)
        })

    return undefined
}

export default ContactSearchApi
